import React, { useEffect, useState } from "react";
import { Row, Col, Table, Card, Button, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { MecordColors } from "Constants/Color";
import { EnquiryDataProps } from "../types";
import { Select as MuiSelect, MenuItem as MuiMenuItem } from "@mui/material";
import StatusRemarksHistory from "./StatusRemarksHistory";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";

import CancelBackButton from "Common/Components/Buttons/CancelBackButton";
import { AttachmentList } from "components/DocumentListing/AttachmentListing";
import CancelButton from "Common/Components/Buttons/CancelButton";
import { formatIndianAmount } from "helpers/formatIndianAmount";

interface EnquiryViewComponentProps {
  details: EnquiryDataProps;
  type: "product" | "service";
  onStatusChange?: (newStatus: string) => void;
  onConvertToQuotation?: () => void;
}

const EnquiryViewComponent: React.FC<EnquiryViewComponentProps> = ({
  details,
  type,
  onStatusChange,
  onConvertToQuotation,
}) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(
    details?.enquiry_status || details?.status || "Open",
  );

  // Map properties with fallbacks to handle various response structures
  const enquiryId = details.enquiry_id || details.id;
  const companyName =
    details.customer_details?.company_name || details.company_name;
  const companyType =
    details.customer_details?.gst_type || details.company_type || "N/A";
  const companyEmail = details.customer_details?.email || details.company_email;
  const industry =
    details.customer_details?.industry || details.industry || "N/A";
  const phoneNumber =
    details.customer_details?.phone_number ||
    details.contact_person_phone_no ||
    "N/A";
  const contactPersons =
    details.customer_details?.contact_details || details.contact_persons || [];
  const agentName = details.agent_name || details.agent?.agent_name || "N/A";
  const status_remarks_and_history = details?.status_remarks_and_history || [];

  const items =
    type === "product"
      ? details.enquiry_product_items
      : type === "service"
        ? details.enquiry_service_items
        : details.item_details || [];

  const chunkArray = (arr: any[], size: number) => {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  };

  const renderShippingAddress = (shipping_address: any) => {
    if (!shipping_address) return "-";

    let addressObj;

    // Case 1: Array
    if (Array.isArray(shipping_address)) {
      // If first element is string, return it directly
      if (typeof shipping_address[0] === "string") return shipping_address[0];
      // If first element is object, use it
      addressObj = shipping_address[0];
    } else if (typeof shipping_address === "object") {
      // Case 2: Object
      addressObj = shipping_address;
    } else if (typeof shipping_address === "string") {
      // Case 3: String
      return shipping_address;
    } else {
      return "-";
    }

    // If we have an object, format it
    if (addressObj && typeof addressObj === "object") {
      const parts = [
        addressObj.address,
        addressObj.city,
        addressObj.state,
        addressObj.country,
        addressObj.pincode ? `- ${addressObj.pincode}` : null,
      ].filter(Boolean);

      return parts.join(", ") || "-";
    }

    return "-";
  };

  return (
    <Card className="shadow-sm rounded-3 border-0">
      <Card.Body>
        {/* 1. Enquiry Details Section */}
        <section className="mb-4">
          <h5 className="mb-4">Enquiry Details</h5>
          <Row className="ms-2">
            <Col md={6}>
              <p>
                <strong>Enquiry No. :</strong> {details.enquiry_number || "N/A"}
              </p>
              <p>
                <strong>Initiated By :</strong> {details.initiated_by || "N/A"}
              </p>

              {type === "service" && (
                <>
                  <p>
                    <strong>Service Number :</strong>{" "}
                    {details.service_number || "N/A"}
                  </p>
                  <p>
                    <strong>UID Number :</strong>{" "}
                    {details.uid_number || "N/A"}
                  </p>
                </>
              )}
              {/* <p><strong>Reference No. :</strong> {details.reference_number || details.reference || "N/A"}</p> */}
              <p>
                <strong>Description :</strong> {details.description || "N/A"}
              </p>
            </Col>
            <Col md={6}>
              <p>
                <strong>Enquiry Date :</strong>{" "}
                {details.enquiry_date ? (
                  <FormattedDate date={details.enquiry_date} />
                ) : (
                  "N/A"
                )}
              </p>
              <p>
                <strong>Source of Enquiry :</strong>{" "}
                {details.source_of_enquiry || "N/A"}
              </p>
              <p>
                <strong>Reference No. :</strong>{" "}
                {details.reference_number || "N/A"}
              </p>
            </Col>
          </Row>
        </section>

        <hr />

        {/* 2. Customer Details Section */}
        <section className="mb-4">
          <h5 className="mb-3">Customer Details</h5>
          <div
            className="p-3 mb-4"
            style={{
              backgroundColor: MecordColors.ActiveBackground,
              borderRadius: "10px",
            }}
          >
            <Row>
              <Col md={6}>
                <p>
                  <strong>Company Name :</strong> {companyName || "N/A"}
                </p>
                <p>
                  <strong>Type :</strong> {companyType || "N/A"}
                </p>
                <p>
                  <strong>Company Email :</strong> {companyEmail || "N/A"}
                </p>
              </Col>
              <Col md={6}>
                <p>
                  <strong>Industry :</strong> {industry}
                </p>
                <p>
                  <strong>Company Phone No. :</strong> {phoneNumber}
                </p>
              </Col>
            </Row>
            <hr />
            <h6 className="fw-bold mb-3">Contact Details</h6>
            <Row>
              <Col md={12}>
                {contactPersons && contactPersons.length > 0 ? (
                  chunkArray(contactPersons, 2).map((pair, rowIndex) => (
                    <Row key={rowIndex} className="mb-3">
                      {/* LEFT CONTACT */}
                      <Col md={6}>
                        {pair[0] && (
                          <div className="mb-2">
                            <p className="mb-1">
                              <strong>Contact Name:</strong>{" "}
                              {pair[0].name ||
                                pair[0].contact_person_name ||
                                "N/A"}
                            </p>
                            <p className="mb-1">
                              <strong>Email:</strong>{" "}
                              {pair[0].email ||
                                pair[0].contact_person_email ||
                                "N/A"}
                            </p>
                            <p className="mb-1">
                              <strong>Phone Number:</strong>{" "}
                              {pair[0].phone_number ||
                                pair[0].contact_person_phone ||
                                "N/A"}
                            </p>
                          </div>
                        )}
                      </Col>

                      {/* RIGHT CONTACT */}
                      <Col md={6}>
                        {pair[1] && (
                          <div className="mb-2">
                            <p className="mb-1">
                              <strong>Contact Name:</strong>{" "}
                              {pair[1].name ||
                                pair[1].contact_person_name ||
                                "N/A"}
                            </p>
                            <p className="mb-1">
                              <strong>Email:</strong>{" "}
                              {pair[1].email ||
                                pair[1].contact_person_email ||
                                "N/A"}
                            </p>
                            <p className="mb-1">
                              <strong>Phone Number:</strong>{" "}
                              {pair[1].phone_number ||
                                pair[1].contact_person_phone ||
                                "N/A"}
                            </p>
                          </div>
                        )}
                      </Col>
                    </Row>
                  ))
                ) : (
                  <p className="text-muted">No contact persons listed.</p>
                )}
              </Col>
            </Row>
          </div>
        </section>

        <hr />

        {/* 3. Agent Details Section */}
        <section className="mb-4">
          <Row>
            {/* Agent Details Column */}
            <Col md={6}>
              <h5 className="fw-bold mb-4" style={{ fontSize: "1rem" }}>
                Agent Details
              </h5>
              <div className="d-flex align-items-center ms-2">
                <span
                  className="fw-bold"
                  style={{ minWidth: "100px", color: "#334155" }}
                >
                  Agent :
                </span>
                <span>{agentName}</span>
              </div>

              {/* Assigned Technician (service only) */}
              {type === "service" && (details as any).assigned_technician && (details as any).assigned_technician.length > 0 && (
                <div className="mt-3">
                  <h5 className="fw-bold mb-2" style={{ fontSize: "1rem" }}>Assigned Technician</h5>
                  <div className="d-flex flex-wrap gap-2 ms-2">
                    {((details as any).assigned_technician as any[]).map((tech: any, idx: number) => {
                      const initials = `${tech.first_name?.[0] || ""}${tech.last_name?.[0] || ""}`.toUpperCase();
                      const colors = [
                        { bg: "#dbeafe", circle: "#2563eb", text: "#1e40af" },
                        { bg: "#dcfce7", circle: "#16a34a", text: "#15803d" },
                        { bg: "#fce7f3", circle: "#db2777", text: "#9d174d" },
                        { bg: "#fef9c3", circle: "#ca8a04", text: "#854d0e" },
                      ];
                      const color = colors[idx % colors.length];
                      return (
                        <div
                          key={tech.user_id || idx}
                          className="d-flex align-items-center p-1 pe-3 rounded"
                          style={{ backgroundColor: color.bg, border: "none" }}
                        >
                          <div
                            className="d-flex align-items-center justify-content-center rounded me-2"
                            style={{
                              width: "24px",
                              height: "24px",
                              backgroundColor: color.circle,
                              color: "#fff",
                              fontSize: "0.7rem",
                              fontWeight: "bold",
                            }}
                          >
                            {initials || "T"}
                          </div>
                          <span style={{ fontSize: "0.85rem", fontWeight: "600", color: "#1e293b" }}>
                            {tech.first_name} {tech.last_name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Col>

            {/* Assigned Members Column */}
            <Col md={6}>
              <h5 className="fw-bold mb-4" style={{ fontSize: "1rem" }}>
                Assign Member
              </h5>
              <div className="d-flex flex-wrap gap-2 ms-2">
                {details.assigned_members &&
                details.assigned_members.length > 0 ? (
                  details.assigned_members.map((member: any, idx: number) => {
                    const initials = `${member.first_name?.[0] || ""}${
                      member.last_name?.[0] || ""
                    }`.toUpperCase();
                    const colors = [
                      { bg: "#fee2e2", circle: "#ef4444", text: "#991b1b" },
                      { bg: "#ccfbf1", circle: "#0d9488", text: "#115e59" },
                      { bg: "#e0e7ff", circle: "#4f46e5", text: "#3730a3" },
                      { bg: "#fef3c7", circle: "#d97706", text: "#92400e" },
                    ];
                    const color = colors[idx % colors.length];

                    return (
                      <div
                        key={member.user_id || idx}
                        className="d-flex align-items-center p-1 pe-3 rounded"
                        style={{ backgroundColor: color.bg, border: "none" }}
                      >
                        <div
                          className="d-flex align-items-center justify-content-center rounded me-2"
                          style={{
                            width: "24px",
                            height: "24px",
                            backgroundColor: color.circle,
                            color: "#fff",
                            fontSize: "0.7rem",
                            fontWeight: "bold",
                          }}
                        >
                          {initials || "U"}
                        </div>
                        <span
                          style={{
                            fontSize: "0.85rem",
                            fontWeight: "600",
                            color: "#1e293b",
                          }}
                        >
                          {member.first_name} {member.last_name}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <span className="text-muted">No members assigned</span>
                )}
              </div>
            </Col>
          </Row>
        </section>

        {/* Follow-up Details (service only) */}
        {type === "service" && (details as any).enquiry_follow_ups?.[0] && (
          <>
            <hr />
            <section className="mb-4">
              <h5 className="mb-3">Follow-up Details</h5>
              <Row className="ms-2">
                <Col md={6}>
                  <p>
                    <strong>Next Follow-up Date :</strong>{" "}
                    {(details as any).enquiry_follow_ups[0].next_follow_up_date ? (
                      <FormattedDate date={(details as any).enquiry_follow_ups[0].next_follow_up_date} />
                    ) : ("N/A")}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Follow-up Remarks :</strong>{" "}
                    {(details as any).enquiry_follow_ups[0].follow_up_remarks || "N/A"}
                  </p>
                </Col>
              </Row>
            </section>
          </>
        )}

        <Row>
          <Col md={6}>
            <h5 className="fw-bold mb-3" style={{ fontSize: "1rem" }}>
              Tags
            </h5>
            <div className="d-flex flex-column ms-2">
              {Array.isArray((details as any).tags) &&
                (details as any).tags.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mt-1">
                    {(details as any).tags.map((tag: string) => (
                      <span
                        key={tag}
                        className="badge bg-light text-dark d-flex align-items-center rounded-2"
                        style={{
                          border: "1px solid #dee2e6",
                          padding: "4px 8px",
                          fontSize: "11px",
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
            </div>
          </Col>
        </Row>

        <hr />

        {/* 4. Items Table Section */}
        <section className="mb-4">
          <h5 className="mb-3">
            {type === "product" ? "Products" : "Services"}
          </h5>
          <div
            className="table-responsive p-3"
            style={{
              backgroundColor: MecordColors.ActiveBackground,
              borderRadius: "10px",
            }}
          >
            <Table hover responsive>
              <thead style={{ backgroundColor: MecordColors.LightGrayColor }}>
                <tr>
                  <th style={{ minWidth: "80px" }}>Sr No.</th>
                  {type === "service" && (
                    <th style={{ minWidth: "140px" }}>Model</th>
                  )}
                  {/* <th>{type === "product" ? "Product" : "Service"}</th> */}
                  <th style={{ minWidth: "140px" }}>Product</th>
                  {/* <th>Category</th>
                                    <th>Sub-Category</th> */}
                  {type === "product" && (
                    <>
                      <th style={{ minWidth: "140px" }}>Make</th>
                      <th style={{ minWidth: "160px" }}>Model</th>
                      <th style={{ minWidth: "80px" }}>Qty</th>
                      <th style={{ minWidth: "100px" }}>Unit</th>
                      <th style={{ minWidth: "140px" }}>Unit Price</th>
                      <th style={{ minWidth: "160px" }}>Total Price</th>
                    </>
                  )}
                  {type === "service" && (
                    <>
                      <th style={{ minWidth: "140px" }}>Serial No.</th>
                      <th style={{ minWidth: "160px" }}>Service Category</th>
                      <th style={{ minWidth: "160px" }}>Service Method</th>
                      <th style={{ minWidth: "160px" }}>Service Type</th>
                      <th style={{ minWidth: "160px" }}>Execution Mode</th>
                      <th style={{ minWidth: "140px" }}>Product Quantity</th>
                      <th style={{ minWidth: "140px" }}>Service Rate</th>
                      <th style={{ minWidth: "180px" }}>Service Amount</th>
                      <th style={{ minWidth: "200px" }}>Service Address</th>
                      <th style={{ minWidth: "160px" }}>Report Complaint</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white">
                {items?.map((item: any, idx: number) => (
                  <tr
                    key={
                      item.enquiry_product_id ||
                      item.enquiry_service_id ||
                      item.id
                    }
                  >
                    <td>{idx + 1}</td>
                    {type === "service" && <td>{item.model_name || "-"}</td>}
                    <td>
                      {item.product_name ||
                        item.service_name ||
                        item.item_name ||
                        "-"}
                    </td>
                    {/* <td>{item.category_name || "-"}</td>
                                        <td>{item.sub_category_name || "-"}</td> */}
                    {type === "product" && (
                      <>
                        <td>{item.make_name || "-"}</td>
                        <td>{item.model_name || "-"}</td>
                        <td>
                          {item.product_quantity ||
                            item.service_quantity ||
                            item.quantity}
                        </td>
                        <td>{item.unit_name || item.unit || "-"}</td>
                        <td>{`${(details as any).currency_symbol || "₹"} ${formatIndianAmount(item.unit_price)}`}</td>
                        <td>
                          {`${(details as any).currency_symbol || "₹"} ${formatIndianAmount(
                            item.product_total ||
                              item.service_total ||
                              item.total_price,
                          )}`}
                        </td>
                      </>
                    )}
                    {type === "service" && (
                      <>
                        <td>{item.serial_no || "-"}</td>
                        <td>{item.service_category || "-"}</td>
                        <td>{item.service_method || "-"}</td>
                        <td>{item.service_type || "-"}</td>
                        <td>{item.execution_mode || "-"}</td>
                        <td>
                          {item.product_quantity ||
                            item.service_quantity ||
                            item.quantity}
                        </td>
                        <td>{`${(details as any).currency_symbol || "₹"} ${formatIndianAmount(item.service_rate)}`}</td>
                        <td>{`${(details as any).currency_symbol || "₹"} ${formatIndianAmount(item.total_service_amount)}`}</td>
                        <td>
                          <span>
                            {renderShippingAddress(item.shipping_address)}
                          </span>
                        </td>

                        <td>{item.reported_complaints || "-"}</td>
                      </>
                    )}
                  </tr>
                ))}
                {(!items || items.length === 0) && (
                  <tr>
                    <td
                      colSpan={type === "service" ? 11 : 11}
                      className="text-center"
                    >
                      No items found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </section>

        <hr />

        {/* 5. Remarks & References Section */}
        <Row>
          <Col md={6}>
            {/* 6. Status Remarks and History Section */}
            <section className="mb-4">
              <h5 className="mb-3">Status Remarks and History</h5>
              <StatusRemarksHistory items={status_remarks_and_history || []} />
            </section>
          </Col>

          <Col md={6}>
            <section className="mb-4">
              <h5 className="mb-3">Remarks & Attachments</h5>
              <Table bordered className="bg-white">
                <tbody>
                  <tr>
                    <td
                      style={{
                        minWidth: "70px",
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Notes
                    </td>
                    <td style={{ whiteSpace: "pre-wrap" }}>
                      {details.notes || "-"}
                    </td>
                  </tr>
                </tbody>
              </Table>
              <div>
                <strong className="d-block mb-2">Attachments:</strong>
                <AttachmentList attachments={details.attachments} />
              </div>
            </section>
          </Col>
        </Row>

        <hr />

        {/* 6. Back Button */}
        <Row>
          <div className="d-flex flex-wrap justify-content-center gap-3">
            <CancelButton
              redirectTo={
                type === "service"
                  ? "/enquiry/service/enquiry_log"
                  : "/enquiry/product/enquiry_log"
              }
              showConfirmation={false}
            />
          </div>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default EnquiryViewComponent;
